import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { InboxMessageDetail, InboxAttachment } from '../../../core/interfaces/communication.interface';
import { InboxService } from '../../../core/services/inbox/inbox.service';
import { MailTemplatesService } from '../../../core/services/mail-templates/mail-templates.service';
import { AlertService } from '../../../core/services/alert/alert.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-inbox-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './inbox-detail.component.html',
  styleUrl: './inbox-detail.component.css',
})
export class InboxDetailComponent implements AfterViewInit {
  @Input() message!: InboxMessageDetail;
  @Input() folder: string = 'inbox';
  @Input() compactMode: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() replySent = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('replyEditor') replyEditor!: ElementRef<HTMLDivElement>;

  showReplyForm = false;
  isReplyAll = false;
  replyHtml = '';
  replyAttachments: File[] = [];
  isSending = false;
  downloadingIndex: number | null = null;

  // Templates
  mailTemplates: any[] = [];
  showTemplateSelector = false;
  templatesLoaded = false;

  constructor(
    private inboxService: InboxService,
    private mailTemplatesService: MailTemplatesService,
    private alertService: AlertService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
  ) {}

  ngAfterViewInit(): void {}

  get sanitizedBody(): SafeHtml {
    const html = this.message.body_html || this.escapeText(this.message.body_text || '');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  get fromDisplay(): string {
    if (!this.message.from || this.message.from.length === 0) return '';
    const f = this.message.from[0];
    return f.name || f.email;
  }

  get fromEmail(): string {
    if (!this.message.from || this.message.from.length === 0) return '';
    return this.message.from[0].email;
  }

  get toDisplay(): string {
    if (!this.message.to || this.message.to.length === 0) return '';
    return this.message.to.map(t => t.name || t.email).join(', ');
  }

  get ccDisplay(): string {
    if (!this.message.cc || this.message.cc.length === 0) return '';
    return this.message.cc.map(c => c.name || c.email).join(', ');
  }

  get replyAllRecipients(): string {
    const all: string[] = [];
    if (this.message.to) all.push(...this.message.to.map(t => t.name || t.email));
    if (this.message.cc) all.push(...this.message.cc.map(c => c.name || c.email));
    return all.join(', ');
  }

  get isSentMessage(): boolean {
    return (this.message.folder || this.folder) === 'sent';
  }

  get messageFolder(): string {
    return this.message.folder || this.folder;
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  getFileIcon(mime: string): string {
    if (mime.includes('pdf')) return 'icon-file-pdf';
    if (mime.includes('image')) return 'icon-picture';
    if (mime.includes('spreadsheet') || mime.includes('excel')) return 'icon-file-excel';
    if (mime.includes('word') || mime.includes('document')) return 'icon-file-word';
    return 'icon-file-empty';
  }

  downloadAttachment(att: InboxAttachment): void {
    this.downloadingIndex = att.index;
    this.inboxService.downloadAttachment(this.message.uid, att.index, this.messageFolder).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = att.name || 'attachment';
        a.click();
        URL.revokeObjectURL(url);
        this.downloadingIndex = null;
      },
      error: (err) => {
        this.downloadingIndex = null;
        if (err.status === 502) {
          this.alertService.error('swal_messages.account_imaps.imap_connection_failed');
        } else {
          this.alertService.error('swal_messages.inbox_messages.inbox_attachment_not_found');
        }
      },
    });
  }

  toggleReply(replyAll: boolean = false): void {
    if (this.showReplyForm && this.isReplyAll === replyAll) {
      this.showReplyForm = false;
      this.isReplyAll = false;
      this.replyHtml = '';
      this.replyAttachments = [];
    } else {
      this.showReplyForm = true;
      this.isReplyAll = replyAll;
    }
  }

  // Rich text editor commands
  execCommand(command: string, value?: string): void {
    document.execCommand(command, false, value);
  }

  insertLink(): void {
    const url = prompt('URL del enlace:');
    if (url) {
      document.execCommand('createLink', false, url);
    }
  }

  onEditorInput(event: Event): void {
    const el = event.target as HTMLElement;
    this.replyHtml = el.innerHTML;
  }

  // Template selector via Swal (same pattern as orders)
  async openTemplateSelector(): Promise<void> {
    const [title, selectLabel, previewLabel, noTemplates, applyBtn, cancelBtn] = await Promise.all([
      this.translate.get('communication.inbox.select_template').toPromise(),
      this.translate.get('mail_modal.select_template').toPromise(),
      this.translate.get('mail_modal.preview').toPromise(),
      this.translate.get('communication.inbox.no_templates').toPromise(),
      this.translate.get('buttons.apply').toPromise(),
      this.translate.get('buttons.cancel').toPromise(),
    ]);

    // Load templates if not loaded
    if (!this.templatesLoaded) {
      await this.loadTemplatesAsync();
    }

    if (this.mailTemplates.length === 0) {
      Swal.fire({ title: title, text: noTemplates, icon: 'info', confirmButtonText: 'OK' });
      return;
    }

    // Build select options
    const options = this.mailTemplates.reduce((acc: any, tpl: any, i: number) => {
      acc[i] = tpl.name;
      return acc;
    }, {});

    const { value: selectedIndex, isConfirmed } = await Swal.fire({
      title: title,
      input: 'select',
      inputOptions: options,
      inputPlaceholder: selectLabel,
      showCancelButton: true,
      confirmButtonText: applyBtn || 'Aplicar',
      cancelButtonText: cancelBtn || 'Cancelar',
      confirmButtonColor: '#3085d6',
      inputValidator: (value) => {
        if (value === '') return selectLabel;
        return null;
      },
      didOpen: () => {
        // Add preview on change
        const select = Swal.getInput();
        const container = Swal.getHtmlContainer();
        if (select && container) {
          // Create preview div
          const previewDiv = document.createElement('div');
          previewDiv.id = 'swal-template-preview';
          previewDiv.style.cssText = 'margin-top:12px;padding:12px;border:1px solid #e6d8fe;border-radius:8px;max-height:200px;overflow-y:auto;font-size:13px;line-height:1.5;text-align:left;display:none;background:#fafbfd;';
          container.appendChild(previewDiv);

          select.addEventListener('change', () => {
            const idx = parseInt(select.value, 10);
            const tpl = this.mailTemplates[idx];
            if (tpl) {
              previewDiv.style.display = 'block';
              previewDiv.innerHTML = `<div style="font-weight:600;font-size:11px;color:#6b7280;margin-bottom:6px;">${previewLabel}</div>`
                + (tpl.subject ? `<div style="font-weight:500;margin-bottom:8px;">${tpl.subject}</div>` : '')
                + `<div style="color:#414b66;">${tpl.body_html || ''}</div>`;
            } else {
              previewDiv.style.display = 'none';
            }
          });
        }
      },
    });

    if (isConfirmed && selectedIndex !== undefined && selectedIndex !== '') {
      const template = this.mailTemplates[parseInt(selectedIndex, 10)];
      if (template) {
        const bodyHtml = template.body_html || '';
        if (this.replyEditor) {
          this.replyEditor.nativeElement.innerHTML = bodyHtml;
        }
        this.replyHtml = bodyHtml;
      }
    }
  }

  private loadTemplatesAsync(): Promise<void> {
    return new Promise((resolve) => {
      this.mailTemplatesService.getAvailableTemplates(1, 50).subscribe({
        next: (res: any) => {
          this.mailTemplates = res.data || [];
          this.templatesLoaded = true;
          resolve();
        },
        error: () => {
          this.mailTemplates = [];
          this.templatesLoaded = true;
          resolve();
        },
      });
    });
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    const newFiles = Array.from(input.files);
    const total = this.replyAttachments.length + newFiles.length;
    if (total > 10) {
      this.alertService.error('communication.inbox.max_attachments');
      return;
    }
    for (const file of newFiles) {
      if (file.size > 10 * 1024 * 1024) {
        this.alertService.error('communication.inbox.file_too_large');
        return;
      }
    }
    this.replyAttachments.push(...newFiles);
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.replyAttachments.splice(index, 1);
  }

  async sendReply(): Promise<void> {
    // If empty body, ask for confirmation
    const editorContent = this.replyEditor?.nativeElement?.innerText?.trim() || this.replyHtml.replace(/<[^>]*>/g, '').trim();
    if (!editorContent) {
      const result = await this.alertService.confirmation(
        'communication.inbox.confirm_empty_reply',
        'communication.inbox.confirm_empty_reply_title',
        'communication.inbox.send',
        'buttons.cancel',
      );
      if (!result.isConfirmed) return;
    }

    // Get HTML from editor
    if (this.replyEditor) {
      this.replyHtml = this.replyEditor.nativeElement.innerHTML;
    }

    this.isSending = true;
    this.alertService.loading('swal_messages.inbox_messages.inbox_sending_reply');

    this.inboxService.reply(
      this.message.uid,
      this.replyHtml,
      this.replyAttachments.length > 0 ? this.replyAttachments : undefined
    ).subscribe({
      next: () => {
        this.isSending = false;
        this.alertService.close();
        this.alertService.successOk('swal_messages.inbox_messages.inbox_reply_sent');
        this.showReplyForm = false;
        this.replyHtml = '';
        this.replyAttachments = [];
        this.replySent.emit();
      },
      error: (err) => {
        this.isSending = false;
        this.alertService.close();
        if (err.status === 422) {
          const errors = err.error?.errors;
          if (errors?.account_settings) {
            this.alertService.error('swal_messages.account_settings.mail_transport_not_configured');
          } else {
            this.alertService.error(errors || 'communication.inbox.reply_error');
          }
        } else if (err.status === 502) {
          this.alertService.error('swal_messages.account_imaps.imap_connection_failed');
        } else {
          this.alertService.error('communication.inbox.reply_error');
        }
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  private escapeText(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/\n/g, '<br>');
  }
}
